import { decodeBase64ToJson } from "../auth/utils";
import { authenticateAndStoreWsId } from "../auth/validator";
import { AuthLoginRequestMessageSchema, Message, CollectionRegistry } from "../types";
import { logger } from "../utils/logger";



export async function handleAuthLoginRequest(
  data: any,
  collections: CollectionRegistry,
  sendMessage: (message: Message) => void
): Promise<void> {
  try {
    logger.debug('[AUTH_LOGIN_REQ] Parsing incoming auth login request');
    const authRequest = AuthLoginRequestMessageSchema.parse(data);
    const { id, payload } = authRequest;

    const login_data = payload.login_data;

    const wsId = authRequest.from.id ;

    logger.info(`[AUTH_LOGIN_REQ ${id}] Processing auth login request from client: ${wsId}`);
    logger.debug(`[AUTH_LOGIN_REQ ${id}] Login data present: ${!!login_data}`);

    if(!login_data){
        logger.error(`[AUTH_LOGIN_REQ ${id}] Login data not found in request`);
        sendDataResponse(id, {
            success: false,
            error: 'Login data not found'
        }, sendMessage, wsId);
        return;
    }


    // Decode base64 data and parse JSON
    logger.debug(`[AUTH_LOGIN_REQ ${id}] Decoding base64 login data`);
    let loginData: any;
    try {
        loginData = decodeBase64ToJson(login_data);
        logger.debug(`[AUTH_LOGIN_REQ ${id}] Login data decoded successfully`);
    } catch (error) {
        const errorMsg = error instanceof Error ? error.message : String(error);
        logger.error(`[AUTH_LOGIN_REQ ${id}] Failed to decode login data: ${errorMsg}`);
        logger.debug(`[AUTH_LOGIN_REQ ${id}] Decode error details:`, error);
        sendDataResponse(id, {
            success: false,
            error: 'Invalid login data format'
        }, sendMessage, wsId);
        return;
    }

    // Extract username/email and password from decoded data
    const { username, email, password } = loginData;
    const identifier = username || email;

    logger.debug(`[AUTH_LOGIN_REQ ${id}] Validating credentials - username: ${username ? '✓' : '✗'}, email: ${email ? '✓' : '✗'}, password: ${password ? '✓' : '✗'}`);

    if (!identifier) {
        logger.error(`[AUTH_LOGIN_REQ ${id}] Username or email not found in login data`);
        sendDataResponse(id, {
            success: false,
            error: 'Username or email is required'
        }, sendMessage, wsId);
        return;
    }

    if (!password) {
        logger.error(`[AUTH_LOGIN_REQ ${id}] Password not found in login data`);
        sendDataResponse(id, {
            success: false,
            error: 'Password not found in login data'
        }, sendMessage, wsId);
        return;
    }


    if(!wsId){
        logger.error(`[AUTH_LOGIN_REQ ${id}] WebSocket ID not found in request`);
        sendDataResponse(id, {
            success: false,
            error: 'WebSocket ID not found'
        }, sendMessage, wsId);
        return;
    }

    logger.info(`[AUTH_LOGIN_REQ ${id}] Credentials validated, authenticating user: ${identifier} username: ${username} email: ${email} password: ${password}`);

    // Authenticate user and store wsId
    logger.debug(`[AUTH_LOGIN_REQ ${id}] Calling authenticateAndStoreWsId for user: ${identifier}`);
    const authResult = await authenticateAndStoreWsId(
        { username, email, password },
        wsId,
        collections
    );

    logger.info(`[AUTH_LOGIN_REQ ${id}] Authentication result for ${identifier}: ${authResult.success ? 'success' : 'failed'}`);
    if (!authResult.success) {
        logger.warn(`[AUTH_LOGIN_REQ ${id}] Authentication failed for ${identifier}: ${authResult.error}`);
    } else {
        logger.info(`[AUTH_LOGIN_REQ ${id}] User ${authResult.username || identifier} authenticated successfully`);
    }

    // Send response
    logger.debug(`[AUTH_LOGIN_REQ ${id}] Sending auth response to client`);
    sendDataResponse(id, authResult, sendMessage, wsId);

    logger.info(`[AUTH_LOGIN_REQ ${id}] ${authResult.success ? '✓' : '✗'} Auth login request completed`);
    return ;
  }
  catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
    const errorStack = error instanceof Error ? error.stack : undefined;

    logger.error(`[AUTH_LOGIN_REQ] Failed to handle auth login request: ${errorMessage}`);
    logger.debug(`[AUTH_LOGIN_REQ] Error stack trace:`, errorStack);

    // Try to send error response
    try {
      const parsedData = data as any;
      if (parsedData?.id) {
        sendDataResponse(parsedData.id, {
          success: false,
          error: `Internal error: ${errorMessage}`
        }, sendMessage, parsedData.from?.id);
      }
    } catch (sendError) {
      logger.error('[AUTH_LOGIN_REQ] Failed to send error response:', sendError);
    }
  }
}


/**
 * Send a data_res response message
 */
function sendDataResponse(
  id: string,
  res: {success: boolean; error?: string; data?: any},
  sendMessage: (message: Message) => void,
  clientId?: string,
): void {
  const response: Message = {
    id,
    type: 'AUTH_LOGIN_RES',
    from: { type: 'data-agent' },
    to: {
        type: 'runtime',
        id: clientId
    },
    payload:{
        ...res,
    }
  };

  logger.debug(`[AUTH_LOGIN_RES ${id}] Sending ${res.success ? 'successful' : 'failed'} auth response to client: ${clientId}`);
  logger.debug(`[AUTH_LOGIN_RES ${id}] Response payload size: ${JSON.stringify(response).length} bytes`);

  if (res.error) {
    logger.debug(`[AUTH_LOGIN_RES ${id}] Error message: ${res.error}`);
  }

  sendMessage(response);
}

